import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-attempted";

/**
 * React.lazy wrapper that survives stale bundle hashes after a redeploy.
 * Retries the dynamic import once, then forces a single hard reload.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      window.sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (error) {
      // Retry once — transient network failures resolve here.
      try {
        const mod = await factory();
        window.sessionStorage.removeItem(RELOAD_KEY);
        return mod;
      } catch (retryError) {
        const alreadyReloaded =
          window.sessionStorage.getItem(RELOAD_KEY) === "true";
        if (!alreadyReloaded) {
          window.sessionStorage.setItem(RELOAD_KEY, "true");
          window.location.reload();
          // Never resolves; the page is reloading.
          return new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}